import { IGif } from '@giphy/js-types'
import { AdDecision } from './ads-endpoint'

/**
 * Updates data within an ad decision.
 * Modifies bottle_data and AdDecision objects.
 * @param AdDecision The decision to process.
 * @param appendAp Whether to append the ap=1 parameter (default: true)
 * @param adProductType The type of ad product (default: undefined)
 * @returns The modified AdDecision object.
 */
export const processDecision = (
    decision?: AdDecision,
    appendAp: boolean = true,
    adProductType?: string
): AdDecision | undefined => {
    if (!decision || !decision.gif) return

    if (decision.click_url) {
        const gif = { ...decision.gif }
        gif.bottle_data = gif.bottle_data ?? {}
        gif.bottle_data.tdata = gif.bottle_data.tdata ?? {}
        gif.bottle_data.tdata.click_out_url = decision.click_url
    }
    const processedGif =
        decision.gif.type === 'video'
            ? processGifVideoAssets(decision.gif, appendAp)
            : processGifImages(decision.gif, appendAp)

    // for subdecisions on xl_carousel_banner, don't append ap=1 to avoid showing "Promoted" pill
    const shouldAppendApForSubdecisions = decision.subdecisions && adProductType !== 'xl_carousel_banner'

    const processedSubdecisions: AdDecision[] | undefined =
        shouldAppendApForSubdecisions && decision.subdecisions
            ? decision.subdecisions.map((sub: AdDecision) =>
                  sub ? processDecision(sub, appendAp, adProductType)! : sub
              )
            : decision.subdecisions

    return {
        ...decision,
        gif: processedGif,
        subdecisions: processedSubdecisions,
    }
}

/**
 * Appends the 'ap=1' query parameter to a given URL.
 * This is used to indicate that the asset is being used in an ad.
 * @param url The URL to modify.
 * @returns The modified URL with the 'ap=1' query parameter.
 */
export const appendApParam = (url: string) => {
    try {
        const u = new URL(url)
        // Avoid duplicating if already present
        if (!u.searchParams.has('ap')) {
            u.searchParams.append('ap', '1')
        }
        return u.toString()
    } catch {
        // Fallback for non-standard URLs
        const hasQuery = url.includes('?')
        const hasAp = /[?&]ap=1(?!\d)/.test(url)
        return hasAp ? url : `${url}${hasQuery ? '&' : '?'}ap=1`
    }
}

/**
 * Processes a GIF's images to append the 'ap=1' parameter to all image URLs.
 * This is used for ad tracking.
 * @param gif The GIF object to process.
 * @param appendAp Whether to append the ap=1 parameter (default: true)
 */
export const processGifImages = (gif: IGif, appendAp: boolean = true) => {
    if (!gif?.images || !appendAp) return gif

    const processedImages = Object.fromEntries(
        Object.entries(gif.images).map(([imageKey, imageValue]) => {
            if (!imageValue || typeof imageValue !== 'object') {
                return [imageKey, imageValue]
            }
            const processedImage = Object.fromEntries(
                Object.entries(imageValue).map(([key, value]) => {
                    if (typeof value === 'string' && /^https?:\/\//.test(value)) {
                        return [key, appendApParam(value)]
                    }
                    return [key, value]
                })
            )
            return [imageKey, processedImage]
        })
    )
    return { ...gif, images: processedImages } as IGif
}

/**
 * Processes a GIF's video assets to append the 'ap=1' parameter to all asset URLs.
 * This is used for ad tracking.
 * @param gif The GIF object to process.
 * @param appendAp Whether to append the ap=1 parameter (default: true)
 */
export const processGifVideoAssets = (gif: IGif, appendAp: boolean = true) => {
    if (!gif.video?.assets || !appendAp) return gif

    const processedAssets = Object.fromEntries(
        Object.entries(gif.video.assets).map(([assetKey, assetValue]) => {
            if (!assetValue || typeof assetValue !== 'object') {
                return [assetKey, assetValue]
            }
            const processedAsset = Object.fromEntries(
                Object.entries(assetValue).map(([key, value]) => {
                    if (typeof value === 'string' && /^https?:\/\//.test(value)) {
                        return [key, appendApParam(value)]
                    }
                    return [key, value]
                })
            )

            return [assetKey, processedAsset]
        })
    )
    return { ...gif, video: { ...gif.video, assets: processedAssets } } as IGif
}
